class PhoneNumberValidator {
  ALLOWED_CHARS_RE = /^\s*\+?[0-9\s\(\)\-]+$/
  HAS_AT_LEAST_ONE_NUMBER_RE = /.*[0-9].*/
  FINNISH_NUMBER_START_RE = /^\s*(?:\+358|0)/
  MIN_NUMBER_NORMALISED_LENGTH = 7
  MAX_NUMBER_NORMALISED_LENGTH = 17

  private cleanNumber(phoneNumber: string): string {
    return phoneNumber.replace(/[\s\-\(\)]/g, '')
  }

  isValid(phoneNumber: string, allowEmptyOrNull: boolean): boolean {
    if (phoneNumber === undefined || phoneNumber.length === 0) {
      return allowEmptyOrNull
    } else if (
      this.normalize(phoneNumber, false).length >= this.MIN_NUMBER_NORMALISED_LENGTH &&
      this.normalize(phoneNumber, false).length <= this.MAX_NUMBER_NORMALISED_LENGTH
    ) {
      return this.ALLOWED_CHARS_RE.test(phoneNumber) && this.HAS_AT_LEAST_ONE_NUMBER_RE.test(phoneNumber)
    }
    return false
  }

  /**
   * Checks if the phone number is a supported phone number (Finnish or valid international).
   * Accepts Finnish numbers (+358, 00358, or single 0 at the start, but not 00 alone).
   * If not Finnish, accepts international numbers (starting with + or 00, but not Finnish),
   * after normalizing 00 to + and checking for 7–17 digits after the prefix.
   * Rejects phone numbers starting with "+0" or "000" (invalid patterns).
   *
   * @param phoneNumber the phone number to check
   * @return true if supported, false otherwise
   */
  isValidPhoneNumber(phoneNumber: string): boolean {
    // Prevent any phone number starting with "+0" or "000"
    if (phoneNumber && (phoneNumber.startsWith('+0') || phoneNumber.startsWith('000'))) {
      return false
    }
    if (!this.isValid(phoneNumber, false)) {
      return false
    }
    if (!this.ALLOWED_CHARS_RE.test(phoneNumber)) {
      return false
    }
    if (this.isValidFinnishMobileNumber(phoneNumber)) {
      return true
    }
    return this.isValidNonFinnishInternationalPhoneNumber(phoneNumber)
  }

  private isValidFinnishMobileNumber(phoneNumber: string): boolean {
    if (!this.isValid(phoneNumber, false)) {
      return false
    }
    // Clean the number first
    const cleanedNumber = this.cleanNumber(phoneNumber)
    const normalized = this.normalize(cleanedNumber, false)

    // If number has explicit international prefix that's not Finnish, it's not a Finnish number
    if (normalized.startsWith('+') && !normalized.startsWith('+358')) {
      return false
    }

    // Check if it's a Finnish number format
    const isFinnishFormat = this.FINNISH_NUMBER_START_RE.test(normalized)
    if (!isFinnishFormat) {
      return false
    }

    // For Finnish numbers, check against valid prefixes
    const numbers = normalized.replace(/[^0-9+]/g, '')
    return this.VALID_PREFIXES.some((prefix) => numbers.startsWith(prefix))
  }

  private isValidNonFinnishInternationalPhoneNumber(phoneNumber: string): boolean {
    // Clean and normalize the phone number
    const cleanedNumber = this.cleanNumber(phoneNumber)
    const normalized = this.normalize(cleanedNumber, false)

    // If it's a Finnish number, this method should return false as it's for non-Finnish numbers only
    if (this.isFinnishAnyNumber(phoneNumber)) {
      return false
    }

    // Check if it's in international format (starts with +)
    if (!normalized.startsWith('+')) {
      return false
    }
    return true
  }

  private isFinnishAnyNumber(phoneNumber: string): boolean {
    // Clean the number first
    const cleanedNumber = this.cleanNumber(phoneNumber)
    const normalized = this.normalize(cleanedNumber, false)

    // If number has explicit international prefix that's not Finnish, it's not a Finnish number
    if (normalized.startsWith('+') && !normalized.startsWith('+358')) {
      return false
    }

    return this.FINNISH_NUMBER_START_RE.test(normalized)
  }

  normalize(phoneNumber: string, allowNull: boolean): string {
    if (!phoneNumber || phoneNumber.length === 0) {
      return allowNull ? '' : phoneNumber
    }

    if (phoneNumber != null) {
      phoneNumber = phoneNumber.trim()
    }

    if (phoneNumber && phoneNumber.length > 0) {
      let normalized = phoneNumber.replace(/[^0-9+]/g, '')

      // Handle numbers starting with 00 (international format)
      if (normalized.startsWith('00')) {
        normalized = '+' + normalized.substring(2)
      }

      // Assume finnish country code if number starts with zero and doesn't have international prefix
      if (normalized.startsWith('0') && !normalized.startsWith('+')) {
        normalized = '+' + '358' + normalized.substring(1)
      }

      // Add plus sign if no international prefix
      if (!normalized.startsWith('+') && normalized.startsWith('358')) {
        normalized = '+' + normalized
      }

      // Remove possible zero after finnish country code
      // Note: For some countries (for example Italy) the zero after country code should not be removed
      if (normalized.startsWith('+3580')) {
        normalized = '+358' + normalized.substring(5)
      }

      return normalized
    }
    return ''
  }

  VALID_PREFIXES = [
    // Phone numbers starting with 04 or 050 are considered valid
    '+3584',
    '+35850',

    // We also allow national subscriber numbers
    // https://www.viestintavirasto.fi/en/internettelephone/numberingoftelecommunicationsnetworks/businessnumbers.html
    '+358101',
    '+358102',
    '+358103',
    '+358104',
    '+358105',
    '+358106',
    '+358107',
    '+358108',
    '+358109',

    '+358201',
    '+3582021',
    '+3582024',
    '+3582025',
    '+3582026',
    '+3582027',
    '+3582028',
    '+3582029',
    '+358203',
    '+358204',
    '+358205',
    '+358206',
    '+358207',
    '+358208',

    '+35829',

    '+358301',
    '+358302',
    '+358303',
    '+358304',
    '+358305',
    '+358306',
    '+358307',
    '+358308',
    '+358309',

    '+35839',

    '+35871',
    '+35873',

    '+35875003',
    '+35875004',
    '+35875005',
    '+35875006',
    '+35875007',
    '+35875008',
    '+35875009',

    '+35875303',
    '+35875304',
    '+35875305',
    '+35875306',
    '+35875307',
    '+35875308',
    '+35875309',

    '+35875323',
    '+35875324',
    '+35875325',
    '+35875326',
    '+35875327',
    '+35875328',
    '+35875329',

    '+35875753',
    '+35875754',
    '+35875755',
    '+35875756',
    '+35875757',
    '+35875758',
    '+35875759',

    '+35875759',

    '+35875983',
    '+35875984',
    '+35875985',
    '+35875986',
    '+35875987',
    '+35875988',
    '+35875989',
  ]
}
export const phoneNumberValidator = new PhoneNumberValidator()
